import type { buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib';
import type { VariantProps } from 'class-variance-authority';
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';
import * as React from 'react';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('flex w-fit justify-end', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex items-center gap-2', className)} {...props} />
  )
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(
        'group hover:bg-grey-800 dark:hover:bg-grey-200 flex h-9 min-h-9 min-w-9 w-fit shrink-0 cursor-pointer items-center justify-center rounded-xl font-medium hover:text-neutral-100 dark:group-hover:text-neutral-200',
        className
      )}
      {...props}
    />
  )
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>,
  'size'
> &
  React.ComponentProps<'a'>;

const PaginationLink = ({ className, isActive, ...props }: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      'border-grey-800 dark:border-grey-100 text-grey-800 dark:text-grey-100 flex h-full min-h-9 min-w-9 w-full cursor-pointer items-center justify-center rounded-xl border px-2 text-sm whitespace-nowrap tabular-nums group-hover:border-0 group-hover:text-neutral-100 dark:group-hover:text-neutral-200',
      className,
      isActive
        ? 'border-0 bg-neutral-200 text-neutral-100 dark:bg-neutral-100 dark:text-neutral-200'
        : ''
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink aria-label="Go to previous page" className={cn(className)} {...props}>
    <ChevronLeftIcon className="text-grey-800 dark:text-grey-100 h-4 w-4 group-hover:text-neutral-100 dark:group-hover:text-neutral-200" />
    <span className="sr-only">Previous</span>
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink aria-label="Go to next page" className={cn(className)} {...props}>
    <span className="sr-only">Next</span>
    <ChevronRightIcon className="text-grey-800 dark:text-grey-100 h-4 w-4 group-hover:text-neutral-100 dark:group-hover:text-neutral-200" />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-full items-end justify-center pb-1.5 font-medium', className)}
    {...props}
  >
    <MoreHorizontalIcon className="text-grey-800 dark:text-grey-100 h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
